import React, { useEffect, useState } from "react";
import axios from "axios";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
    Users, ShieldCheck, ShieldX, 
    Mail, Phone, Calendar, 
    CheckCircle2, XCircle, Loader2,
    Search, Store, Camera,
    Edit, MapPin, Check, X
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export const AdminSellers = () => {
    const [sellers, setSellers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [updatingId, setUpdatingId] = useState(null);
    const [editingAddressId, setEditingAddressId] = useState(null);
    const [editAddressText, setEditAddressText] = useState("");

    useEffect(() => {
        fetchSellers();
    }, []);

    const fetchSellers = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/marketplace/admin/sellers`);
            setSellers(res.data);
        } catch (error) {
            console.error("Error fetching sellers:", error);
            toast.error("Pa kapab chaje lis machann yo.");
        } finally {
            setLoading(false);
        }
    };

    const handleToggleApproval = async (sellerId, currentStatus) => {
        setUpdatingId(sellerId);
        try {
            await axios.post(`${API}/marketplace/admin/sellers/${sellerId}/approval`, {
                is_approved: !currentStatus
            });
            toast.success(currentStatus ? "Machann sa a kounye a PA apwouve." : "Machann sa a apwouve avèk siksè!");
            fetchSellers();
        } catch (error) {
            toast.error("Erè nan chanje estati machann nan.");
        } finally {
            setUpdatingId(null);
        }
    };

    const handleLogoUpload = async (e, sellerId) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const formData = new FormData();
        formData.append("file", file);

        setUpdatingId(sellerId);
        toast.info("Ap chaje logo a...");

        try {
            const uploadRes = await axios.post(`${API}/marketplace/upload`, formData);
            if (uploadRes.data.url) {
                // Ensure we use the correct backend URL if relative path returned
                const imageUrl = uploadRes.data.url.startsWith("http") ? uploadRes.data.url : window.location.origin + uploadRes.data.url;
                await axios.put(`${API}/marketplace/admin/sellers/${sellerId}`, {
                    logo_url: imageUrl
                });
                toast.success("Logo chanje avèk siksè!");
                fetchSellers();
            }
        } catch (err) {
            console.error("Upload error:", err);
            toast.error("Pa kapab chaje logo a.");
        } finally {
            setUpdatingId(null);
        }
    };

    const handleSaveAddress = async (sellerId) => {
        if (updatingId === sellerId) return;
        setUpdatingId(sellerId);
        try {
            await axios.put(`${API}/marketplace/admin/sellers/${sellerId}`, {
                address: editAddressText
            });
            toast.success("Adrès chanje avèk siksè!");
            setEditingAddressId(null);
            fetchSellers();
        } catch(error) {
            toast.error("Erè nan sove adrès sa.");
        } finally {
            setUpdatingId(null);
        }
    };

    const filteredSellers = sellers.filter(s => 
        s.fullname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.store_name?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const formatDate = (timestamp) => {
        return new Date(timestamp * 1000).toLocaleDateString('fr-HT', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    return (
        <AdminLayout>
            <div className="space-y-8">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-black text-[#232F3E] tracking-tighter italic uppercase">
                            Jere <span className="text-[#FF9900]">Machann</span>
                        </h1>
                        <p className="text-gray-400 font-bold uppercase tracking-widest text-[10px] mt-1">
                            Apwouve machann yo anvan yo kòmanse vann sou Marketplace la.
                        </p>
                    </div>

                    <div className="relative w-full md:w-72">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input 
                            placeholder="Chèche machann..." 
                            className="pl-10 h-11 rounded-xl border-gray-100 shadow-sm"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>

                <Card className="border-0 shadow-2xl rounded-[35px] overflow-hidden bg-white">
                    <CardHeader className="bg-gray-50/50 p-8 border-b border-gray-100">
                        <div className="flex items-center justify-between">
                            <CardTitle className="flex items-center gap-4 text-xl font-black italic tracking-tighter">
                                <Users className="w-6 h-6 text-[#FF9900]" />
                                Lis Tout Machann Yo
                            </CardTitle>
                            <span className="bg-[#232F3E] text-[#FF9900] px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg">
                                {filteredSellers.length} MACHANN
                            </span>
                        </div>
                    </CardHeader>
                    <CardContent className="p-0">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-32 gap-4">
                                <Loader2 className="w-12 h-12 animate-spin text-[#FF9900]" />
                                <p className="text-xs font-black text-gray-400 uppercase tracking-widest">Ap chaje lis machann yo...</p>
                            </div>
                        ) : filteredSellers.length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="bg-gray-50/50 text-[10px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100">
                                            <th className="py-5 px-8 text-left">Machann / Magazen</th>
                                            <th className="py-5 px-8 text-left">Kontak</th>
                                            <th className="py-5 px-8 text-left">Dat Enskripsyon</th>
                                            <th className="py-5 px-8 text-left">Lajan nan Bous</th>
                                            <th className="py-5 px-8 text-left">Estati</th>
                                            <th className="py-5 px-8 text-center">Aksyon</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50">
                                        {filteredSellers.map((seller) => (
                                            <tr key={seller.id} className="hover:bg-gray-50/80 transition-colors group">
                                                <td className="py-6 px-8">
                                                    <div className="flex items-center gap-4">
                                                        <div className="relative group/logo">
                                                            <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center text-[#232F3E] overflow-hidden border-2 border-gray-100 shadow-sm relative z-0">
                                                                {seller.logo_url ? (
                                                                    <img src={seller.logo_url} alt="Logo" className="w-full h-full object-cover" />
                                                                ) : (
                                                                    <Store className="w-6 h-6" />
                                                                )}
                                                            </div>
                                                            <label className="absolute -bottom-2 -right-2 w-8 h-8 bg-white border-2 border-gray-100 rounded-full flex items-center justify-center text-[#FF9900] shadow-lg cursor-pointer hover:bg-[#FF9900] hover:text-white transition-colors z-10" title="Chanje Logo">
                                                                <Camera className="w-4 h-4" />
                                                                <input type="file" className="hidden" accept="image/*" onChange={(e) => handleLogoUpload(e, seller.id)} disabled={updatingId === seller.id} />
                                                            </label>
                                                        </div>
                                                        <div>
                                                            <p className="font-black text-[#232F3E] text-base tracking-tight leading-none mb-1">{seller.fullname}</p>
                                                            <p className="text-[10px] text-[#FF9900] font-black uppercase tracking-widest italic">{seller.store_name || "Pa gen non magazen"}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="py-6 px-8">
                                                    <div className="flex flex-col gap-1">
                                                        <div className="flex items-center gap-2 text-xs font-bold text-gray-600">
                                                            <Mail className="w-3 h-3 text-gray-400" /> {seller.email}
                                                        </div>
                                                        <div className="flex items-center gap-2 text-xs font-bold text-gray-600">
                                                            <Phone className="w-3 h-3 text-gray-400" /> {seller.phone}
                                                        </div>
                                                        <div className="flex items-center gap-2 text-xs font-bold text-gray-600 mt-2 border-t pt-2 border-gray-100">
                                                            <MapPin className="w-3 h-3 text-gray-400" /> 
                                                            {editingAddressId === seller.id ? (
                                                                <div className="flex items-center gap-1">
                                                                    <Input 
                                                                        className="h-7 w-28 text-[10px] px-2 shadow-inner bg-gray-50 border-gray-200" 
                                                                        value={editAddressText} 
                                                                        onChange={(e) => setEditAddressText(e.target.value)} 
                                                                        autoFocus 
                                                                        placeholder="Antre zòn nan..."
                                                                    />
                                                                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-green-500 hover:bg-green-50" onClick={() => handleSaveAddress(seller.id)}>
                                                                        <Check className="w-4 h-4" />
                                                                    </Button>
                                                                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:bg-red-50" onClick={() => setEditingAddressId(null)}>
                                                                        <X className="w-4 h-4" />
                                                                    </Button>
                                                                </div>
                                                            ) : (
                                                                <>
                                                                    <span className="truncate max-w-[120px]">{seller.address || "Ayiti (Defo)"}</span>
                                                                    <button onClick={() => { setEditingAddressId(seller.id); setEditAddressText(seller.address || ""); }} className="text-gray-400 hover:text-[#FF9900] transition-colors ml-auto p-1 rounded-md hover:bg-gray-100">
                                                                        <Edit className="w-3 h-3" />
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="py-6 px-8 text-xs font-bold text-gray-400 uppercase">
                                                    <div className="flex items-center gap-2">
                                                        <Calendar className="w-3.5 h-3.5" /> {formatDate(seller.created_at)}
                                                    </div>
                                                </td>
                                                <td className="py-6 px-8 text-xs font-bold text-[#232F3E]">
                                                    <div className="flex items-center gap-2 text-[#FF9900] bg-[#FF9900]/10 px-3 py-1.5 rounded-lg w-fit font-black italic">
                                                        {seller.wallet_balance !== undefined ? `${seller.wallet_balance.toLocaleString()} HTG` : "0 HTG"}
                                                    </div>
                                                </td>
                                                <td className="py-6 px-8">
                                                    <span className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 w-fit shadow-sm ${
                                                        seller.is_approved 
                                                        ? 'bg-green-500 text-white' 
                                                        : 'bg-amber-100 text-amber-600 border border-amber-200'
                                                    }`}>
                                                        {seller.is_approved ? (
                                                            <><CheckCircle2 className="w-3.5 h-3.5" /> Apwouve</>
                                                        ) : (
                                                            <><XCircle className="w-3.5 h-3.5" /> Atant</>
                                                        )}
                                                    </span>
                                                </td>
                                                <td className="py-6 px-8 text-center">
                                                    <Button
                                                        onClick={() => handleToggleApproval(seller.id, seller.is_approved)}
                                                        disabled={updatingId === seller.id}
                                                        className={`h-10 px-6 rounded-xl font-black text-[10px] uppercase tracking-widest shadow-lg transition-all ${
                                                            seller.is_approved 
                                                            ? 'bg-red-50 text-red-500 hover:bg-red-500 hover:text-white border-2 border-red-100 hover:border-red-500' 
                                                            : 'bg-[#FF9900] hover:bg-black text-white shadow-[#FF9900]/20'
                                                        }`}
                                                    >
                                                        {updatingId === seller.id ? (
                                                            <Loader2 className="w-4 h-4 animate-spin" />
                                                        ) : seller.is_approved ? (
                                                            <><ShieldX className="w-4 h-4 mr-2" /> BLOKE</>
                                                        ) : (
                                                            <><ShieldCheck className="w-4 h-4 mr-2" /> APWOUVE</>
                                                        )}
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-center py-32 text-gray-400 bg-gray-50/30">
                                <Users className="w-20 h-20 mx-auto mb-6 opacity-10" />
                                <h3 className="text-lg font-black text-[#232F3E] uppercase tracking-tighter italic">Pa gen okenn machann</h3>
                                <p className="text-[10px] font-black uppercase tracking-widest text-gray-300 mt-2">N ap tann premye machann ou an enskri.</p>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </AdminLayout>
    );
};
